import { useCallback, useEffect, useRef, useState } from "react";
import { persistPlannedSampleSize } from "../utils/plannedSampleSize";

const SAMPLE_SIZE_SAVE_DEBOUNCE_MS = 500;

interface PlannedSampleSizePersistenceInput {
  currentStep: number;
  questionnaireDraftId: string | null;
  numRespondents: number | null;
}

interface SampleSizeSaveBook {
  draftId: string | null;
  generation: number;
  lastPersistedSampleSize: number | null;
  saveTarget: number | null;
}

type SampleSizeLaunchResult = "launched" | "skipped" | "aborted";

interface InFlightSampleSizeSave {
  draftId: string;
  generation: number;
  task: Promise<void>;
}

function savesForDraftGeneration(
  saves: Array<InFlightSampleSizeSave>,
  draftId: string | null,
  generation: number,
): Array<InFlightSampleSizeSave> {
  if (draftId == null) {
    return [];
  }
  return saves.filter(
    (save) => save.draftId === draftId && save.generation === generation,
  );
}

/**
 * Persist the review-step sample size, and keep that write from landing
 * while a launch is reading the same draft.
 *
 * Bookkeeping is scoped to the draft id and a generation that bumps when
 * the draft changes. A save started for the previous draft cannot mark the
 * next draft as already saved.
 *
 * A new size is written immediately while a save is already in flight,
 * including a return to the size on screen before that baseline save has
 * been recorded. A debounced follow-up is cancelled when review closes,
 * and the in-flight size would otherwise be what a reload shows.
 *
 * In-flight saves are tracked per draft and generation. Launch waits only
 * for the draft it is about to read. A stalled PATCH for another draft
 * cannot race that read, so it must not hold launch open.
 */
export function usePlannedSampleSizePersistence(
  input: PlannedSampleSizePersistenceInput,
): {
  launchAfterSampleSizeSettles: (
    launch: () => Promise<void>,
  ) => Promise<SampleSizeLaunchResult>;
} {
  const { currentStep, questionnaireDraftId, numRespondents } = input;
  const [suspended, setSuspended] = useState(false);
  const suspendedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef<Array<InFlightSampleSizeSave>>([]);
  const launchingRef = useRef(false);
  const mountedRef = useRef(true);
  const stepRef = useRef(currentStep);
  const bookRef = useRef<SampleSizeSaveBook>({
    draftId: null,
    generation: 0,
    lastPersistedSampleSize: null,
    saveTarget: null,
  });
  stepRef.current = currentStep;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const startSave = useCallback((draftId: string, sampleSize: number, generation: number) => {
    bookRef.current.saveTarget = sampleSize;
    const task = persistPlannedSampleSize(draftId, sampleSize)
      .then((saved) => {
        const current = bookRef.current;
        if (
          saved &&
          current.generation === generation &&
          current.draftId === draftId &&
          current.saveTarget === sampleSize
        ) {
          current.lastPersistedSampleSize = sampleSize;
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to save planned sample size:", error);
      });
    const entry: InFlightSampleSizeSave = { draftId, generation, task };
    inFlightRef.current.push(entry);
    void task.finally(() => {
      inFlightRef.current = inFlightRef.current.filter((save) => save !== entry);
    });
  }, []);

  useEffect(() => {
    const draftId = questionnaireDraftId;
    if (bookRef.current.draftId !== draftId) {
      bookRef.current = {
        draftId,
        generation: bookRef.current.generation + 1,
        lastPersistedSampleSize: null,
        saveTarget: null,
      };
    }

    const sampleSize = numRespondents;
    const book = bookRef.current;
    if (suspended || currentStep < 3 || !draftId || sampleSize == null) {
      return;
    }

    // An in-flight save already passed the debounce. The next target has to
    // join that queue now: a timer started here is cleared if review closes
    // before it fires, and the in-flight size is what stays stored.
    const saveInFlight =
      savesForDraftGeneration(inFlightRef.current, draftId, book.generation)
        .length > 0;
    if (
      saveInFlight ||
      (book.lastPersistedSampleSize === sampleSize &&
        book.saveTarget !== sampleSize)
    ) {
      if (book.saveTarget !== sampleSize) {
        startSave(draftId, sampleSize, book.generation);
      }
      return;
    }

    const generation = book.generation;
    book.saveTarget = sampleSize;
    const timeoutId = window.setTimeout(() => {
      if (suspendedRef.current || bookRef.current.generation !== generation) {
        return;
      }
      if (bookRef.current.saveTarget !== sampleSize) {
        return;
      }
      startSave(draftId, sampleSize, generation);
    }, SAMPLE_SIZE_SAVE_DEBOUNCE_MS);
    timerRef.current = timeoutId;

    return () => {
      window.clearTimeout(timeoutId);
      if (timerRef.current === timeoutId) {
        timerRef.current = null;
      }
    };
  }, [currentStep, questionnaireDraftId, numRespondents, startSave, suspended]);

  const launchAfterSampleSizeSettles = useCallback(
    async (launch: () => Promise<void>): Promise<SampleSizeLaunchResult> => {
      if (launchingRef.current) {
        return "skipped";
      }
      launchingRef.current = true;
      // Generation moves on every draft id change, including away and back.
      // The id alone matches again after A→B→A, so it cannot see that trip.
      const draftIdAtLaunch = bookRef.current.draftId;
      const generationAtLaunch = bookRef.current.generation;
      const stepAtLaunch = stepRef.current;
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      suspendedRef.current = true;
      setSuspended(true);
      const pending = savesForDraftGeneration(
        inFlightRef.current,
        draftIdAtLaunch,
        generationAtLaunch,
      ).map((save) => save.task);
      try {
        await Promise.all(pending);
        const leftTheDraft =
          !mountedRef.current ||
          bookRef.current.generation !== generationAtLaunch ||
          stepRef.current !== stepAtLaunch;
        if (leftTheDraft) {
          return "aborted";
        }
        await launch();
        return "launched";
      } finally {
        launchingRef.current = false;
        suspendedRef.current = false;
        setSuspended(false);
      }
    },
    [],
  );

  return { launchAfterSampleSizeSettles };
}
